import React, { useState, useEffect, useMemo } from 'react';

function TagSelector({ mode = 'single', tags, conversations = [], selectedTags, onSave, onClose, onCreateTag, onEditTag, onDeleteTag, onExportTag, filterTag: activeFilterTag = '', onSelectTagForFilter }) {
  const [currentSelection, setCurrentSelection] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#0084ff');
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [tagToDelete, setTagToDelete] = useState(null);

  // Estado de edición inline
  const [editingTagId, setEditingTagId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    const ids = selectedTags ? selectedTags.map(t => t.id) : [];
    setCurrentSelection(ids);
  }, [selectedTags]);

  const toggleTag = (tagId) => {
    setCurrentSelection(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const isSelected = (tagId) => currentSelection.some(id => String(id) === String(tagId));

  const filteredTags = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [...tags].sort((a, b) => a.name.localeCompare(b.name));
    return [...tags].filter(t => t.name.toLowerCase().includes(term)).sort((a, b) => a.name.localeCompare(b.name));
  }, [tags, search]);

  const handleSave = () => onSave(currentSelection);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;
    await onCreateTag(newTagName.trim(), newTagColor);
    setNewTagName('');
    setIsCreating(false);
  };

  const tagCounts = useMemo(() => {
    const counts = {};
    const hasBackendCount = (tags || []).some(t => t.conversation_count !== undefined);
    (tags || []).forEach(t => {
      counts[t.id] = t.conversation_count !== undefined ? Number(t.conversation_count) : 0;
    });
    if (!hasBackendCount) {
      (conversations || []).forEach(conv => {
        (conv.tags || []).forEach(t => {
          if (counts[t.id] !== undefined) counts[t.id]++;
        });
      });
    }
    return counts;
  }, [tags, conversations]);

  const sortedTagsForManage = useMemo(() => {
    const term = search.trim().toLowerCase();
    let arr = [...(tags || [])];
    if (term) arr = arr.filter(t => t.name.toLowerCase().includes(term));
    return arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [tags, search]);

  const startEdit = (tag) => {
    setEditingTagId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color || '#0084ff');
  };

  const cancelEdit = () => {
    setEditingTagId(null);
    setEditName('');
    setEditColor('');
  };

  const saveEdit = async (tagId) => {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      await onEditTag(tagId, editName.trim(), editColor);
      setEditingTagId(null);
    } catch (e) {
      console.error('Error editando:', e);
    } finally {
      setEditSaving(false);
    }
  };

  // ===================== MODO MANAGE =====================
  if (mode === 'manage') {
    return (
      <>
        {/* Confirmación de eliminar */}
        {tagToDelete && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
            onClick={() => setTagToDelete(null)}>
            <div style={{ background: '#1f2c34', borderRadius: '14px', padding: '24px', maxWidth: '320px', width: '88%', boxShadow: '0 16px 40px rgba(0,0,0,0.4)' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '20px', marginBottom: '10px' }}>🗑️</div>
              <p style={{ margin: '0 0 8px', fontSize: '15px', color: '#e9edef', fontWeight: 600 }}>¿Eliminar etiqueta?</p>
              <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#8696a0' }}>
                "<strong style={{ color: '#e9edef' }}>{tagToDelete.name}</strong>" será eliminada permanentemente.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setTagToDelete(null)}
                  style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#8696a0', cursor: 'pointer', fontSize: '13px' }}>
                  Cancelar
                </button>
                <button type="button" onClick={() => { onDeleteTag(tagToDelete.id); setTagToDelete(null); }}
                  style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#d32f2f', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Overlay */}
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', justifyContent: 'flex-start', alignItems: 'stretch' }}
          onClick={onClose}>
          {/* Panel principal */}
          <div style={{
            width: 'min(400px, 92vw)',
            background: 'linear-gradient(180deg, #1f2c34 0%, #111b21 100%)',
            color: '#e9edef',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '6px 0 30px rgba(0,0,0,0.4)',
            animation: 'slideInLeft 0.25s ease-out',
            overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
              <button type="button" onClick={onClose}
                style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: '4px', fontSize: '22px', lineHeight: 1, flexShrink: 0 }}>
                ←
              </button>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#e9edef', flex: 1 }}>
                Gestionar Etiquetas
              </h3>
              <span style={{ fontSize: '12px', color: '#8696a0', background: 'rgba(255,255,255,0.08)', padding: '3px 10px', borderRadius: '20px' }}>
                {(tags || []).length} etiquetas
              </span>
            </div>

            {/* Buscador */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#8696a0' }}>🔍</span>
                <input
                  type="text"
                  placeholder="Buscar etiqueta..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '9px 12px 9px 34px',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '10px',
                    color: '#e9edef', fontSize: '14px', outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Formulario de nueva etiqueta */}
            {isCreating && (
              <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,168,132,0.08)', flexShrink: 0 }}>
                <div style={{ fontSize: '12px', color: '#00a884', fontWeight: 600, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nueva etiqueta
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div
                      title="Cambiar color"
                      onClick={() => document.getElementById('manage-new-color').click()}
                      style={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: newTagColor,
                        cursor: 'pointer',
                        border: '2px solid rgba(255,255,255,0.25)',
                      }} />
                    <input id="manage-new-color" type="color" value={newTagColor}
                      onChange={e => setNewTagColor(e.target.value)}
                      style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
                  </div>
                  <input
                    type="text"
                    placeholder="Nombre de la etiqueta..."
                    value={newTagName}
                    onChange={e => setNewTagName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateSubmit(e)}
                    autoFocus
                    style={{
                      flex: 1, padding: '9px 12px',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px', color: '#e9edef', fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setIsCreating(false); setNewTagName(''); }}
                    style={{ padding: '7px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#8696a0', cursor: 'pointer', fontSize: '13px' }}>
                    Cancelar
                  </button>
                  <button type="button" onClick={handleCreateSubmit} disabled={!newTagName.trim()}
                    style={{
                      padding: '7px 18px', borderRadius: '8px', border: 'none',
                      background: newTagName.trim() ? '#00a884' : '#2a3942',
                      color: newTagName.trim() ? '#fff' : '#8696a0',
                      cursor: newTagName.trim() ? 'pointer' : 'not-allowed',
                      fontSize: '13px', fontWeight: 600
                    }}>
                    ✓ Crear
                  </button>
                </div>
              </div>
            )}

            {/* Lista scrollable de etiquetas */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {/* Fila: Todas las conversaciones */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '13px 18px', cursor: 'pointer',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  background: !activeFilterTag ? 'rgba(0,168,132,0.12)' : 'transparent',
                  transition: 'background 0.15s'
                }}
                onClick={() => onSelectTagForFilter && onSelectTagForFilter('')}
              >
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#8696a0', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#e9edef' }}>Todas las conversaciones</div>
                  <div style={{ fontSize: '11px', color: '#8696a0', marginTop: '1px' }}>Sin filtrar por etiqueta</div>
                </div>
                {!activeFilterTag && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00a884' }} />}
              </div>

              {sortedTagsForManage.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: '#8696a0', fontSize: '14px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏷️</div>
                  {search ? 'No hay etiquetas que coincidan' : 'No hay etiquetas. Pulsa + para crear una.'}
                </div>
              ) : (
                sortedTagsForManage.map(tag => (
                  <div key={tag.id}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      background: activeFilterTag === String(tag.id) ? 'rgba(0,168,132,0.1)' : 'transparent',
                    }}>

                    {editingTagId !== tag.id ? (
                      /* Fila normal */
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', cursor: 'pointer' }}
                        onClick={() => onSelectTagForFilter && onSelectTagForFilter(tag.id)}>
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%',
                          background: tag.color || '#0084ff', flexShrink: 0,
                          boxShadow: `0 0 0 2px rgba(255,255,255,0.08)`
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#e9edef', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {tag.name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#8696a0', marginTop: '1px' }}>
                            {(tagCounts[tag.id] ?? 0)} conversaciones
                          </div>
                        </div>
                        {activeFilterTag === String(tag.id) && (
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00a884', flexShrink: 0 }} />
                        )}
                        {/* Botones de acción */}
                        <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <button type="button" title="Editar etiqueta" onClick={() => startEdit(tag)}
                            style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: '5px 6px', borderRadius: '6px', fontSize: '14px', lineHeight: 1 }}>
                            ✏️
                          </button>
                          <button type="button" title="Exportar Excel" onClick={() => onExportTag(tag.id)}
                            style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: '5px 6px', borderRadius: '6px', fontSize: '14px', lineHeight: 1 }}>
                            📊
                          </button>
                          <button type="button" title="Eliminar" onClick={() => setTagToDelete(tag)}
                            style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', padding: '5px 6px', borderRadius: '6px', fontSize: '16px', lineHeight: 1 }}>
                            ×
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Formulario de edición inline */
                      <div style={{ padding: '12px 14px', background: 'rgba(0,168,132,0.1)' }}>
                        <div style={{ fontSize: '11px', color: '#00a884', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Editando etiqueta
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          {/* Color picker cuadrado */}
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div
                              title="Cambiar color"
                              onClick={() => document.getElementById(`edit-color-${tag.id}`).click()}
                              style={{
                                width: 34, height: 34, borderRadius: '8px',
                                background: editColor, cursor: 'pointer',
                                border: '2px solid rgba(255,255,255,0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px'
                              }}>
                              🎨
                            </div>
                            <input id={`edit-color-${tag.id}`} type="color" value={editColor}
                              onChange={e => setEditColor(e.target.value)}
                              style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} />
                          </div>
                          {/* Input del nombre */}
                          <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && editName.trim()) saveEdit(tag.id);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                            style={{
                              flex: 1, padding: '8px 12px',
                              background: 'rgba(255,255,255,0.1)',
                              border: '1px solid rgba(0,168,132,0.5)',
                              borderRadius: '8px', color: '#e9edef',
                              fontSize: '14px', outline: 'none',
                              boxShadow: '0 0 0 2px rgba(0,168,132,0.15)'
                            }}
                          />
                        </div>
                        {/* Vista previa en tiempo real */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', padding: '6px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                          <span style={{ fontSize: '11px', color: '#8696a0' }}>Vista previa:</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: editColor }} />
                            <span style={{ fontSize: '13px', color: '#e9edef', fontWeight: 500 }}>{editName || '...'}</span>
                          </div>
                        </div>
                        {/* Botones */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button type="button" onClick={cancelEdit}
                            style={{ padding: '6px 14px', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.12)', background: 'transparent', color: '#8696a0', cursor: 'pointer', fontSize: '12px' }}>
                            Cancelar
                          </button>
                          <button type="button" onClick={() => saveEdit(tag.id)} disabled={!editName.trim() || editSaving}
                            style={{
                              padding: '6px 16px', borderRadius: '7px', border: 'none',
                              background: editName.trim() ? '#00a884' : '#2a3942',
                              color: editName.trim() ? '#fff' : '#8696a0',
                              cursor: editName.trim() ? 'pointer' : 'not-allowed',
                              fontSize: '12px', fontWeight: 600
                            }}>
                            {editSaving ? '...' : '✓ Guardar'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Footer: botón crear nueva etiqueta */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, background: 'rgba(0,0,0,0.15)' }}>
              <button
                type="button"
                onClick={() => { setIsCreating(true); setSearch(''); cancelEdit(); }}
                style={{
                  width: '100%', padding: '11px', borderRadius: '10px',
                  border: '1px dashed rgba(0,168,132,0.5)',
                  background: 'rgba(0,168,132,0.08)',
                  color: '#00a884', cursor: 'pointer', fontSize: '14px',
                  fontWeight: 600, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '8px', transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span> Nueva etiqueta
              </button>
            </div>
          </div>
        </div>
        <style>{`@keyframes slideInLeft { from { transform: translateX(-100%); opacity: 0.8; } to { transform: translateX(0); opacity: 1; } }`}</style>
      </>
    );
  }

  // ===================== MODO SINGLE / BULK =====================
  return (
    <div className="modal-overlay" onClick={onClose}>
      {tagToDelete && (
        <div className="confirm-overlay" onClick={() => setTagToDelete(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <p className="confirm-message">¿Eliminar la etiqueta <strong>"{tagToDelete.name}"</strong> permanentemente?</p>
            <div className="confirm-actions">
              <button type="button" className="confirm-cancel-btn" onClick={() => setTagToDelete(null)}>Cancelar</button>
              <button type="button" className="confirm-delete-btn" onClick={() => { onDeleteTag(tagToDelete.id); setTagToDelete(null); }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Etiquetas del Chat</h3>
            <p className="modal-subtitle">Organiza tus conversaciones con etiquetas rápidas.</p>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Cerrar">&times;</button>
        </div>

        <div className="modal-body">
          <div className="tags-search-row">
            <input type="text" placeholder="Buscar etiqueta..." value={search}
              onChange={e => setSearch(e.target.value)} className="tags-search-input" />
            <span className="tags-counter">{currentSelection.length} seleccionadas</span>
          </div>
          <div className="tags-grid">
            {filteredTags.map(tag => (
              <div key={tag.id}
                className={`tag-chip-item ${isSelected(tag.id) ? 'selected' : ''}`}
                onClick={() => toggleTag(tag.id)}
                style={{
                  '--tag-color': tag.color,
                  backgroundColor: isSelected(tag.id) ? tag.color : '#f0f2f5',
                  color: isSelected(tag.id) ? '#fff' : '#54656f',
                  border: isSelected(tag.id) ? 'none' : '1px solid #e9edef'
                }}>
                <span className="tag-chip-label">{tag.name}</span>
                <div className="tag-chip-actions">
                  <button className="action-item-btn"
                    onClick={e => { e.stopPropagation(); onExportTag(tag.id); }} title="Descargar Excel (.xlsx)">📊</button>
                  <button className="action-item-btn"
                    onClick={e => { e.stopPropagation(); setTagToDelete(tag); }} title="Eliminar etiqueta">&times;</button>
                </div>
              </div>
            ))}
          </div>
          <div className="create-tag-section">
            {!isCreating ? (
              <button type="button" className="add-tag-chip" onClick={() => setIsCreating(true)}>
                <span className="add-tag-chip-icon">+</span> Añadir etiqueta
              </button>
            ) : (
              <div className="create-tag-form">
                <div className="create-tag-row">
                  <label className="color-swatch-label">Color</label>
                  <button type="button" className="color-swatch-btn" style={{ backgroundColor: newTagColor }}
                    onClick={() => document.getElementById('tag-color-picker').click()} title="Elegir color" />
                  <input id="tag-color-picker" type="color" value={newTagColor}
                    onChange={e => setNewTagColor(e.target.value)} className="color-picker-hidden" />
                  <input type="text" placeholder="Nombre de la etiqueta" value={newTagName}
                    onChange={e => setNewTagName(e.target.value)} autoFocus className="new-tag-input"
                    onKeyDown={e => e.key === 'Enter' && handleCreateSubmit(e)} />
                </div>
                <div className="create-tag-buttons">
                  <button type="button" onClick={() => { setIsCreating(false); setNewTagName(''); }} className="create-cancel-btn">Cancelar</button>
                  <button type="button" onClick={handleCreateSubmit} disabled={!newTagName.trim()} className="create-submit-btn">Crear</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #e9edef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setCurrentSelection([])} className="link-danger">Desmarcar todos</button>
          <div className="modal-footer-actions">
            <button className="cancel-btn" onClick={onClose}>Cancelar</button>
            <button className="save-btn" onClick={handleSave}>Guardar Cambios</button>
          </div>
        </div>
      </div>
      <style>{`
        .modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background-color:rgba(0,0,0,0.6);display:flex;justify-content:center;align-items:center;z-index:1000;backdrop-filter:blur(2px)}
        .confirm-overlay{position:absolute;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1001}
        .confirm-dialog{background:#fff;border-radius:14px;padding:22px 24px;max-width:320px;width:90%;box-shadow:0 12px 28px rgba(0,0,0,0.2)}
        .confirm-message{margin:0 0 20px;font-size:15px;color:#111b21;line-height:1.45}
        .confirm-message strong{color:#111b21}
        .confirm-actions{display:flex;justify-content:flex-end;gap:10px}
        .confirm-cancel-btn{padding:10px 18px;border-radius:10px;border:1px solid #e9edef;background:#fff;color:#54656f;font-size:14px;font-weight:500;cursor:pointer}
        .confirm-cancel-btn:hover{background:#f5f6f6}
        .confirm-delete-btn{padding:10px 18px;border-radius:10px;border:none;background:#d32f2f;color:#fff;font-size:14px;font-weight:600;cursor:pointer}
        .confirm-delete-btn:hover{background:#b71c1c}
        .modal-content{background-color:white;padding:0;border-radius:16px;width:90%;max-width:440px;box-shadow:0 10px 25px rgba(0,0,0,0.2);display:flex;flex-direction:column;overflow:hidden;animation:popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)}
        @keyframes popIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}
        .modal-header{padding:20px;display:flex;justify-content:space-between;align-items:center;background-color:#f0f2f5}
        .modal-header h3{margin:0;font-size:18px;color:#111b21}
        .modal-subtitle{margin:4px 0 0;font-size:13px;color:#667781}
        .close-button{background:none;border:none;font-size:24px;color:#54656f;cursor:pointer;line-height:1}
        .modal-body{flex:1;padding:20px;overflow-y:auto;max-height:60vh}
        .tags-search-row{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:16px}
        .tags-search-input{flex:1;border-radius:999px;border:1px solid #e9edef;padding:6px 12px;font-size:13px;outline:none}
        .tags-search-input:focus{border-color:#00a884;box-shadow:0 0 0 1px rgba(0,168,132,0.16)}
        .tags-counter{font-size:12px;color:#8696a0;white-space:nowrap}
        .tags-grid{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:20px}
        .tag-chip-item{padding:8px 14px;border-radius:20px;font-size:14px;cursor:pointer;transition:all 0.2s;display:flex;align-items:center;gap:6px;position:relative;user-select:none}
        .tag-chip-item.selected{box-shadow:0 2px 6px rgba(0,168,132,0.35)}
        .tag-chip-label{max-width:160px;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
        .tag-chip-actions{display:flex;gap:4px}
        .tag-chip-item:hover{transform:translateY(-1px);box-shadow:0 2px 5px rgba(0,0,0,0.08)}
        .tag-chip-item:hover .action-item-btn{opacity:1}
        .action-item-btn{background:rgba(0,0,0,0.1);border:none;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:10px;color:inherit;cursor:pointer;opacity:0;transition:opacity 0.2s;margin-left:2px}
        .action-item-btn:hover{background:rgba(0,0,0,0.2)}
        .create-tag-section{border-top:1px solid #e9edef;padding-top:14px;margin-top:4px}
        .add-tag-chip{padding:8px 14px;border-radius:20px;font-size:14px;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:6px;background:#e9edef;border:1px solid #d1d7db;color:#00a884;font-weight:600}
        .add-tag-chip:hover{background:#d9dde1;border-color:#00a884;color:#008f6f}
        .add-tag-chip-icon{font-size:16px;line-height:1}
        .create-tag-form{background:#f7f8f9;border:1px solid #e9edef;border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:12px}
        .create-tag-row{display:flex;align-items:center;gap:10px}
        .color-swatch-label{font-size:12px;color:#667781;font-weight:500;flex-shrink:0}
        .color-swatch-btn{width:36px;height:36px;border-radius:10px;border:2px solid rgba(0,0,0,0.12);cursor:pointer;flex-shrink:0;padding:0}
        .color-swatch-btn:hover{border-color:#00a884;transform:scale(1.05)}
        .color-picker-hidden{position:absolute;width:0;height:0;opacity:0;pointer-events:none}
        .new-tag-input{flex:1;border:1px solid #e9edef;border-radius:10px;padding:10px 14px;font-size:14px;outline:none}
        .new-tag-input:focus{border-color:#00a884;box-shadow:0 0 0 2px rgba(0,168,132,0.2)}
        .create-tag-buttons{display:flex;justify-content:flex-end;gap:8px}
        .create-cancel-btn{padding:8px 16px;border-radius:10px;border:1px solid #e9edef;background:#fff;color:#54656f;font-size:13px;font-weight:500;cursor:pointer}
        .create-cancel-btn:hover{background:#f0f2f5}
        .create-submit-btn{padding:8px 20px;border-radius:10px;border:none;background:#00a884;color:#fff;font-size:13px;font-weight:600;cursor:pointer}
        .create-submit-btn:disabled{background:#a0b0a8;cursor:not-allowed}
        .create-submit-btn:not(:disabled):hover{background:#029273}
        .link-danger{background:none;border:none;color:#d32f2f;font-size:13px;cursor:pointer;font-weight:500;padding:0}
        .modal-footer-actions{display:flex;gap:10px}
        .cancel-btn{padding:8px 16px;border-radius:20px;border:1px solid #e9edef;background-color:white;color:#54656f;cursor:pointer;font-size:14px;font-weight:500}
        .save-btn{padding:8px 24px;border-radius:20px;border:none;background-color:#00a884;color:white;cursor:pointer;font-size:14px;font-weight:600;box-shadow:0 2px 5px rgba(0,168,132,0.3)}
        .save-btn:hover{background-color:#029273}
        .cancel-btn:hover{background-color:#f5f6f6}
      `}</style>
    </div>
  );
}

export default TagSelector;
